import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  VStack,
  Alert,
  AlertIcon,
  Text,
  Button,
  Center,
  Flex,
} from "@chakra-ui/react";
import { getTimetable, Sport, SportingEvent } from "../data/api";
import { groupBy } from "lodash";
import moment from "moment";
import { TimetableDay, TimetableDaySkeleton } from "./TimetableDay";
import SportSelector from "./SportSelector";
import useAuth from "../hooks/useAuth";
import SubscribeSwitch from "./SubscribeSwitch";

// TODO: events in the past behind a show more button
// TODO: when filtered by a sport, add a quick-link button to subscribe

const TimetableTab = () => {
  const [events, setEvents] = useState<SportingEvent[] | undefined>();
  const [filter, setFilter] = useState<Sport | "ALL">("ALL");
  const [showAllEvents, setShowAllEvents] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (token) getTimetable(token).then(setEvents);
  }, [token]);

  const eventsByDate = useCallback(
    (events: SportingEvent[]) => {
      return Object.entries(
        groupBy(
          events
            .filter((event) =>
              filter === "ALL" ? true : event.team.sport.title === filter.title
            )
            .sort((a, b) =>
              a.start < b.start ? -1 : a.start > b.start ? 1 : 0
            ),
          (e) => moment(e.start).format("YYYY-MM-DD")
        )
      );
    },
    [filter]
  );

  const mainEvents = useMemo(() => {
    if (showAllEvents && events) return eventsByDate(events);
    if (!events) return [];
    return eventsByDate(events).filter(([date]) =>
      moment(date).isSameOrAfter(moment().startOf("day"))
    );
  }, [events, eventsByDate, showAllEvents]);

  const pastEvents = useMemo(() => {
    if (!events) return [];
    return eventsByDate(events).filter(([date]) =>
      moment(date).isBefore(moment().startOf("day"))
    );
  }, [events, eventsByDate]);

  return (
    <Box>
      <SportSelector sportSelected={setFilter} />
      {filter !== "ALL" ? <SubscribeSwitch sport={filter} /> : null}
      <VStack alignItems="start" gap={5}>
        {pastEvents.length > 0 ? (
          <Flex justifyContent="center" w="full" p={1}>
            <Button
              size="sm"
              colorScheme="gray"
              onClick={() => setShowAllEvents((prev) => !prev)}
            >
              {showAllEvents ? "Hide" : "Show"} past events
            </Button>
          </Flex>
        ) : null}
        {mainEvents ? (
          mainEvents.length > 0 ? (
            mainEvents.map(([date, events], i) => (
              <TimetableDay date={date} events={events} key={i} />
            ))
          ) : (
            <Alert status="warning" variant="left-accent">
              <AlertIcon />
              <Text fontSize="md">
                Could not find any events for that sport
              </Text>
            </Alert>
          )
        ) : (
          [...new Array(4)].map((e, i) => <TimetableDaySkeleton key={i} />)
        )}
      </VStack>
    </Box>
  );
};

export default TimetableTab;
